/**
 * Canvas Observer - Performance optimization for canvas animations
 * Pauses/resumes animations based on visibility using IntersectionObserver
 */

export class CanvasObserver {
  constructor(options = {}) {
    this.canvases = new Map();
    this.observerOptions = {
      root: null,
      rootMargin: options.rootMargin || "100px", // Start animating slightly before visible
      threshold: options.threshold || 0.01,
    };
    this.observer = new IntersectionObserver(this.handleIntersection.bind(this), this.observerOptions);
  }

  /**
   * Register a canvas with start/stop callbacks
   * @param {HTMLCanvasElement} canvas - The canvas element
   * @param {Function} onStart - Callback to start animation
   * @param {Function} onStop - Callback to stop animation
   */
  observe(canvas, onStart, onStop) {
    if (!canvas || !(canvas instanceof HTMLCanvasElement)) {
      console.warn("CanvasObserver: Invalid canvas element");
      return;
    }

    this.canvases.set(canvas, {
      onStart,
      onStop,
      isAnimating: false,
    });

    this.observer.observe(canvas);

    // Check if already in viewport and start immediately if so
    const rect = canvas.getBoundingClientRect();
    const isInViewport = rect.top < window.innerHeight + 100 && rect.bottom > -100;
    if (isInViewport) {
      const data = this.canvases.get(canvas);
      if (data && !data.isAnimating) {
        data.isAnimating = true;
        if (data.onStart) {
          data.onStart();
        }
      }
    }
  }

  /**
   * Unregister a canvas
   * @param {HTMLCanvasElement} canvas
   */
  unobserve(canvas) {
    const data = this.canvases.get(canvas);
    if (data && data.isAnimating && data.onStop) {
      data.onStop();
    }
    this.canvases.delete(canvas);
    this.observer.unobserve(canvas);
  }

  /**
   * Handle intersection changes
   * @param {IntersectionObserverEntry[]} entries
   */
  handleIntersection(entries) {
    entries.forEach((entry) => {
      const canvas = entry.target;
      const data = this.canvases.get(canvas);

      if (!data) return;

      if (entry.isIntersecting && !data.isAnimating) {
        // Canvas is visible - start animation
        data.isAnimating = true;
        if (data.onStart) {
          data.onStart();
        }
      } else if (!entry.isIntersecting && data.isAnimating) {
        // Canvas is not visible - stop animation
        data.isAnimating = false;
        if (data.onStop) {
          data.onStop();
        }
      }
    });
  }

  /**
   * Cleanup - stop observing all canvases
   */
  disconnect() {
    this.canvases.forEach((data, canvas) => {
      if (data.isAnimating && data.onStop) {
        data.onStop();
      }
    });
    this.canvases.clear();
    this.observer.disconnect();
  }
}

// Singleton instance for global use
let globalObserver = null;

/**
 * Get or create the global canvas observer instance
 * @param {Object} options - Observer options
 * @returns {CanvasObserver}
 */
export function getCanvasObserver(options) {
  if (!globalObserver) {
    globalObserver = new CanvasObserver(options);
  }
  return globalObserver;
}
